import { MenuItem } from '@mui/material';

/**
 * Capitaliza las palabras de una cadena
 * @param {string} str - Cadena a capitalizar
 * @returns {string} - Cadena capitalizada
 */
export const capitalizeWords = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Genera elementos de menú para años
 * @param {number} startYear - Año inicial (por defecto 1990)
 * @param {number} endYear - Año final (por defecto año actual)
 * @returns {Array} - Array de elementos MenuItem
 */
export const generateYearMenuItems = (startYear = 1990, endYear = new Date().getFullYear()) => {
  const items = [];
  for (let i = endYear; i >= startYear; i--) {
    items.push(
      <MenuItem key={i} value={i.toString()}>
        {i}
      </MenuItem>
    );
  }
  return items;
};

/**
 * Genera elementos de menú para meses
 * @returns {Array} - Array de elementos MenuItem para meses
 */
export const generateMonthMenuItems = () => {
  const months = [
    { value: '1', label: 'Ene' },
    { value: '2', label: 'Feb' },
    { value: '3', label: 'Mar' },
    { value: '4', label: 'Abr' },
    { value: '5', label: 'May' },
    { value: '6', label: 'Jun' },
    { value: '7', label: 'Jul' },
    { value: '8', label: 'Ago' },
    { value: '9', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dic' },
  ];

  return months.map((month) => (
    <MenuItem key={month.value} value={month.value}>
      {month.label}
    </MenuItem>
  ));
};

/**
 * Genera el asunto del email
 * @param {string} planName - Nombre del plan
 * @param {string} contractNumber - Número de contrato
 * @returns {string} - Asunto del email
 */
export const generateEmailSubject = (planName, contractNumber) => {
  return `${planName} - Nro de Orden: ${contractNumber}`;
};

/**
 * Genera el mensaje del email
 * @param {string} formattedName - Nombre formateado del suscriptor
 * @param {Object} emailData - Datos del email
 * @param {string} planName - Nombre del plan
 * @returns {string} - Mensaje del email
 */
export const generateEmailMessage = (formattedName, emailData, planName) => {
  const couponsText = emailData.coupons
    .map(
      (coupon) =>
        `- Cupón Mes: ${coupon.month} Año: ${coupon.year} Cuotas: ${coupon.fee_number1} - ${coupon.fee_number2}`
    )
    .join('\n');

  return (
    `Estimado ${formattedName}, Nro de Orden: ${emailData.contract_number}\n\n` +
    `En el presente correo le hacemos envío de los cupones que se detallan a continuación:\n` +
    couponsText +
    `\n\nAtte.\n\n${planName}\n\nArchivo Adjunto: NroOrden1-Año2023-Grupo0.pdf`
  );
};

/**
 * Obtiene el cupón más reciente de una lista de cupones
 * @param {Array} coupons - Lista de cupones
 * @returns {Object|null} - Cupón más reciente o null
 */
export const getMostRecentCoupon = (coupons) => {
  if (!coupons || !Array.isArray(coupons) || coupons.length === 0) {
    return null;
  }

  return coupons.sort((a, b) => new Date(b.year, b.month - 1) - new Date(a.year, a.month - 1))[0];
};

/**
 * Valida si un email es válido
 * @param {string} email - Email a validar
 * @returns {boolean} - True si es válido
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Formatea los datos de cupones para mostrar en la tabla
 * @param {Array} coupons - Lista de cupones
 * @returns {string} - Cupones formateados para mostrar
 */
export const formatCouponsForDisplay = (coupons) => {
  return coupons
    .map(
      (coupon) =>
        `Mes: ${coupon.month} Año: ${coupon.year} Cuotas: ${coupon.fee_number1} - ${coupon.fee_number2}`
    )
    .join(', ');
};

/**
 * Filtra elementos únicos por una propiedad específica
 * @param {Array} array - Array a filtrar
 * @param {string} property - Propiedad por la cual filtrar
 * @returns {Array} - Array con elementos únicos
 */
export const getUniqueByProperty = (array, property) => {
  const seen = new Set();
  return array.filter((item) => {
    const value = item[property];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

/**
 * Construye el payload para envío de emails de deudas
 * @param {Array} emailsData - Datos de emails de deudas a enviar
 * @returns {Object} - Payload para la API de deudas
 */
export const buildDebtEmailsPayload = (emailsData) => {
  return {
    emails_deudas: emailsData.map((emailData) => {
      const formattedName = capitalizeWords(`${emailData.first_name} ${emailData.last_name}`);
      
      // Calcular cantidad de cuotas
      const cantidad = emailData.coupons ? emailData.coupons.length : 1;
      
      // Para el importe, usar un valor por defecto o calcularlo si está disponible
      // TODO: Obtener el importe real de la deuda desde el backend
      const importe = emailData.debt_amount || '0';
      
      return {
        suscriptor: formattedName,
        email: emailData.email || 'no-user-email@gmail.com',
        contrato_id: emailData.contract_number || '',
        cantidad: cantidad,
        importe: importe
      };
    }),
  };
};

/**
 * Construye el payload para envío de emails
 * @param {Array} emailsData - Datos de emails a enviar
 * @param {Array} plans - Lista de planes
 * @returns {Object} - Payload para la API
 */
export const buildEmailsPayload = (emailsData, plans) => {
  return {
    emails: emailsData.map((emailData) => {
      const formattedName = capitalizeWords(`${emailData.first_name} ${emailData.last_name}`);
      const mostRecentCoupon = getMostRecentCoupon(emailData.coupons);
      const uuidForFile = mostRecentCoupon?.uuid;
      const planName =
        plans.find((plan) => plan.uuid === emailData.contract?.uuid_plan)?.name || '';
      console.log(emailData.subscriber)
      return {
        email: emailData.subscriber?.email,
        subject: generateEmailSubject(planName, emailData.contract_number),
        text_message: generateEmailMessage(formattedName, emailData, planName),
        name: formattedName,
        order_number: emailData.contract_number,
        coupons: emailData.coupons,
        plan_name: planName,
        attachment: 'NroOrden1-Año2023-Grupo0.pdf',
        note: emailData.note || '',
        uuidForFile: uuidForFile,
      };
    }),
  };
};
